import React, {
  useState,
  useMemo,
  useEffect,
} from 'react';
import {
  Drawer,
  Input,
  Select,
  Table,
  Row,
  Col,
} from 'antd';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { CloseOutlined } from '@ant-design/icons';

import DrawerSubmitFooter from '../../../../../common/containers/DrawerSubmitFooter';
import BorderlessButton from '../../../../../common/buttons/BorderlessButton';
import { FOOTER_HEIGHT } from '../../../../../constants/Styles';
import { getIdMap, includesTerm } from '../../../../../helpers/helpers';

const NAME_COLUMNS = [
  {
    title: 'Name',
    dataIndex: 'name',
  },
];

const VARIANT_CONFIG = {
  user: {
    title: 'Add Users',
    description: 'Search team members to add to the grid',
    searchPlaceholder: 'Search users',
    emptyHint: 'Selected users below or',
    rowType: 'user',
    teamProperty: 'members',
  },
  equipment: {
    title: 'Add Equipment',
    description: 'Search equipment to add to the grid, or select by team',
    searchPlaceholder: 'Search equipment',
    emptyHint: 'Selected equipment below or',
    rowType: 'equipment',
    teamProperty: 'equipment',
  },
};

const applyTeamSelection = ({
  variant,
  newTeams,
  selectedTeams,
  selectedRows,
  entityMap,
  teamMap,
}) => {
  const getIdsFromTeams = (teamIds) => {
    const idSet = new Set();
    const { teamProperty } = VARIANT_CONFIG[variant];
    teamIds.forEach((teamId) => {
      const team = teamMap[teamId];
      if (team?.[teamProperty]) {
        team[teamProperty].forEach((item) => {
          idSet.add(item.id || item);
        });
      }
    });
    return Array.from(idSet);
  };

  if (!newTeams?.length) {
    const removedIds = new Set(getIdsFromTeams(selectedTeams));
    const nextRows = selectedRows.filter((row) => !removedIds.has(row.id));
    return { nextRows, nextTeamIds: [] };
  }

  const selectedTeamSet = new Set(selectedTeams);
  const addedTeams = [];
  const unchangedTeams = [];
  const removedTeams = [];

  newTeams.forEach((teamId) => {
    if (!selectedTeamSet.has(teamId)) {
      addedTeams.push(teamId);
      return;
    }
    unchangedTeams.push(teamId);
    selectedTeamSet.delete(teamId);
  });

  selectedTeamSet.forEach((teamId) => {
    removedTeams.push(teamId);
  });

  const addedIds = getIdsFromTeams(addedTeams);
  const unchangedIds = getIdsFromTeams(unchangedTeams);
  const removedIdSet = new Set(getIdsFromTeams(removedTeams));

  const idsFromStillSelectedTeams = new Set([...addedIds, ...unchangedIds]);
  const allSelectedIds = new Set([
    ...addedIds,
    ...unchangedIds,
    ...selectedRows.map((row) => row.id),
  ]);

  const { rowType } = VARIANT_CONFIG[variant];
  const nextRows = [];
  allSelectedIds.forEach((entityId) => {
    if (
      (removedIdSet.has(entityId) && !idsFromStillSelectedTeams.has(entityId))
      || !entityMap[entityId]
    ) return;
    const entity = entityMap[entityId];
    if (!entity.active) return;
    nextRows.push({ id: entity.id, name: entity.name, type: rowType });
  });

  return { nextRows, nextTeamIds: newTeams };
};

export default function TimeTrackingGridWizardAddMemberDrawer({
  variant,
  visible,
  onClose,
  divisions,
  selectedRows,
  setSelectedRows,
}) {
  const users = useSelector((state) => state.users.users);
  const equipment = useSelector((state) => state.equipment.equipment);
  const teams = useSelector((state) => state.teams.teams);
  const stateDivisions = useSelector((state) => state.settings.divisions);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTableRows, setSelectedTableRows] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);

  const config = VARIANT_CONFIG[variant];

  const teamMap = useMemo(() => getIdMap(teams), [teams]);
  const divisionSet = useMemo(() => new Set(divisions), [divisions]);

  const divisionList = useMemo(() => {
    if (variant === 'user') {
      const relevantUserIds = new Set();
      divisions.forEach((divisionId) => {
        const {
          [divisionId]: {
            users: divisionUserIds = new Set(),
          } = {},
        } = stateDivisions;
        divisionUserIds.forEach((userId) => relevantUserIds.add(userId));
      });
      return users.filter((user) => user.active && relevantUserIds.has(user.id));
    }
    return equipment.filter((item) => (
      item.active && item.divisionIds?.some((divisionId) => divisionSet.has(divisionId))
    ));
  }, [variant, users, equipment, stateDivisions, divisions, divisionSet]);

  const entityMap = useMemo(
    () => getIdMap(divisionList),
    [divisionList],
  );

  useEffect(() => {
    if (visible) {
      setSelectedTableRows(selectedRows);
    }
  }, [visible, selectedRows]);

  useEffect(() => {
    if (!visible) {
      setSearchTerm('');
      setSelectedTableRows([]);
      setSelectedTeams([]);
    }
  }, [visible]);

  const divisionTeams = useMemo(() => (
    teams.filter((team) => (
      team.active && team.divisionIds?.some?.((divisionId) => divisionSet.has(divisionId))
    )).map((team) => ({
      label: team.name,
      value: team.id,
    }))
  ), [teams, divisionSet]);

  const filteredDivisionList = useMemo(() => (
    divisionList.filter((entity) => includesTerm(entity.name, searchTerm))
  ), [divisionList, searchTerm]);

  const onSubmit = () => {
    if (setSelectedRows) setSelectedRows(selectedTableRows);
    onClose();
  };

  const onTeamSelect = (newTeams) => {
    const { nextRows, nextTeamIds } = applyTeamSelection({
      variant,
      newTeams,
      selectedTeams,
      selectedRows: selectedTableRows,
      entityMap,
      teamMap,
    });
    setSelectedTableRows(nextRows);
    setSelectedTeams(nextTeamIds);
  };

  const selectedIdSet = useMemo(
    () => new Set(selectedTableRows.map((row) => row.id)),
    [selectedTableRows],
  );

  const { rowType } = config;

  const onSelectRow = (record, checked) => {
    if (checked) {
      setSelectedTableRows((prev) => {
        if (prev.some((row) => row.id === record.id)) return prev;
        return [...prev, { id: record.id, name: record.name, type: rowType }];
      });
    } else {
      setSelectedTableRows((prev) => prev.filter((row) => row.id !== record.id));
    }
  };

  const clearSelection = () => {
    setSelectedTableRows([]);
    setSelectedTeams([]);
  };

  return (
    <Drawer
      title={config.title}
      visible={visible}
      width={400}
      onClose={onClose}
      bodyStyle={{
        padding: 0,
        marginBottom: FOOTER_HEIGHT,
        overflowY: 'auto',
      }}
      headerStyle={{ border: 'none' }}
    >
      <div className="time-tracking-grid-wizard-add-costcode-drawer-description">
        {config.description}
      </div>
      <div className="time-tracking-grid-wizard-add-costcode-drawer-search-container">
        <Input.Search
          value={searchTerm}
          placeholder={config.searchPlaceholder}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="time-tracking-grid-wizard-add-costcode-drawer-search"
        />
      </div>
      <Row className="time-tracking-grid-wizard-add-user-drawer-table-header">
        <Col span={12} className="time-tracking-grid-wizard-add-user-drawer-table-header-left-col">
          {!selectedTableRows?.length && (
            <div className="sub-description">{config.emptyHint}</div>
          )}
          {!!selectedTableRows?.length && (
            <>
              <div className="time-tracking-grid-wizard-add-costcode-drawer-project-row-selected-costcode-count">
                {selectedTableRows?.length} selected
              </div>
              <BorderlessButton
                title="Clear"
                iconNode={<CloseOutlined />}
                onClick={clearSelection}
                style={{ width: 80 }}
                iconLeft
              />
            </>
          )}
        </Col>
        <Col span={12}>
          <Select
            options={divisionTeams}
            placeholder="Select by team"
            onChange={onTeamSelect}
            className="time-tracking-grid-wizard-add-user-drawer-team-select"
            mode="multiple"
            allowClear
            value={selectedTeams}
            showSearch
            optionFilterProp="label"
          />
        </Col>
      </Row>
      <Table
        dataSource={filteredDivisionList}
        columns={NAME_COLUMNS}
        rowKey="id"
        pagination={false}
        rowSelection={{
          selectedRowKeys: selectedTableRows.map((row) => row.id),
          preserveSelectedRowKeys: true,
          onSelect: onSelectRow,
          hideSelectAll: true,
          getCheckboxProps: () => ({
            className: 'ontraccr-checkbox',
          }),
          className: 'time-tracking-grid-wizard-add-user-drawer-table-row-selection',
        }}
        onHeaderRow={() => ({
          className: 'time-tracking-grid-wizard-add-user-drawer-table-header-row',
        })}
        rowClassName={(record) => (
          `time-tracking-grid-wizard-add-user-drawer-table-row-selection ${selectedIdSet.has(record.id) ? 'selected' : ''}`
        )}
      />
      <DrawerSubmitFooter
        onClose={onClose}
        submitTitle="Done"
        onSubmit={onSubmit}
      />
    </Drawer>
  );
}

const wizardRowPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['user', 'equipment']).isRequired,
});

TimeTrackingGridWizardAddMemberDrawer.propTypes = {
  variant: PropTypes.oneOf(['user', 'equipment']).isRequired,
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  divisions: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedRows: PropTypes.arrayOf(wizardRowPropType).isRequired,
  setSelectedRows: PropTypes.func.isRequired,
};
